import { Box } from "@mui/material";
// Per-group UI imports — commented out while per-group UI is disabled
// import React, { useMemo } from "react";
// import { Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Button, Stack } from "@mui/material";
// import { ExpandMore, ExpandLess } from "@mui/icons-material";
// import PropertyMappingSelector from "../../../components/PropertyMappingSelector";
// import type { PropertyMappings } from "../../../components/PropertyMappingSelector/PropertyMappingSelector.types";
import type { PropertyMappingProps } from "./PropertyMapping.types";
import { usePropertyMappingViewModel } from "./PropertyMapping.vm";
import GlobalPropertyMappingSelector from "../../../components/GlobalPropertyMappingSelector";
import "./PropertyMapping.css";

// Per-group UI helpers — commented out while per-group UI is disabled
// /** Extract file names from file paths (last segment without extension) */
// function getFileNames(filePaths: string[]): string {
// 	return filePaths
// 		.map((path) => {
// 			const lastSegment = path.split("/").pop() || path;
// 			return lastSegment.replace(/\.(shp|dbf|shx|prj|cpg|json|geojson|zip|kml|kmz)$/i, "");
// 		})
// 		.join(", ");
// }
//
// /** Extract directory path from file paths (everything before the last segment) */
// function getDirectoryPath(filePaths: string[]): string | null {
// 	if (filePaths.length === 0) return null;
// 	const firstPath = filePaths[0];
// 	const lastSlashIndex = firstPath.lastIndexOf("/");
// 	if (lastSlashIndex === -1) return null;
// 	return firstPath.substring(0, lastSlashIndex);
// }

/**
 * PropertyMapping step component
 *
 * Allows users to configure which file properties map to which job fields.
 * Supports global "Apply to All" mappings and per-group customization.
 */
export default function PropertyMapping(props: PropertyMappingProps) {
	const { company } = props;
	const vm = usePropertyMappingViewModel(props);

	// Per-group UI callbacks — commented out while per-group UI is disabled
	// const { geodataGroups, propertyMappings } = props;
	// const groupCallbacks = useMemo(() => {
	// 	const callbacks: Record<string, (mappings: PropertyMappings) => void> = {};
	// 	for (const group of geodataGroups) {
	// 		callbacks[group.id] = (mappings: PropertyMappings) => vm.handleGroupMappingChange(group.id, mappings);
	// 	}
	// 	return callbacks;
	// }, [geodataGroups, vm.handleGroupMappingChange]);

	return (
		<Box className="property-mapping" sx={{ p: 2 }}>
			{/* Global Property Mapping Selector */}
			<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
				<GlobalPropertyMappingSelector
					onApplyMapping={vm.handleGlobalMappingApply}
					availableProperties={vm.allAvailableProperties}
					autoGuessedMappings={vm.aggregatedAutoGuesses}
					company={company}
					showMoreFields={vm.showMoreFields}
					onToggleShowMore={vm.handleToggleShowMore}
				/>
			</Box>

			{/* Per-Group UI disabled — kept for future re-enablement */}
			{/* <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="subtitle1">
					Per-Group Mappings ({vm.totalGroups} {vm.totalGroups === 1 ? "group" : "groups"})
				</Typography>
				<Stack direction="row" spacing={1}>
					<Button
						size="small"
						startIcon={<ExpandMore sx={{ transform: "rotate(180deg)" }} />}
						onClick={vm.handleExpandAll}
						disabled={vm.expandedGroups.size === geodataGroups.length}
					>
						Expand All
					</Button>
					<Button
						size="small"
						startIcon={<ExpandLess sx={{ transform: "rotate(180deg)" }} />}
						onClick={vm.handleCollapseAll}
						disabled={vm.expandedGroups.size === 0}
					>
						Collapse All
					</Button>
				</Stack>
			</Box>

			<Box sx={{ overflow: "hidden" }}>
				{geodataGroups.map((group) => {
					const isExpanded = vm.expandedGroups.has(group.id);
					const groupMappings = propertyMappings[group.id];

					return (
						<Accordion
							key={group.id}
							expanded={isExpanded}
							onChange={() => vm.handleToggleGroup(group.id)}
							sx={{ mb: 1, overflow: "hidden" }}
						>
							<AccordionSummary
								expandIcon={<ExpandMore />}
								sx={{ overflow: "hidden", "& .MuiAccordionSummary-content": { overflow: "hidden", minWidth: 0 } }}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", overflow: "hidden" }}>
									<Box sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
										<Typography variant="subtitle2" noWrap>
											{getFileNames(group.filePaths)}
										</Typography>
										{getDirectoryPath(group.filePaths) && (
											<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
												{getDirectoryPath(group.filePaths)}
											</Typography>
										)}
									</Box>

									<Chip
										label={`${group.featureCount} ${group.geometryType === "polygon" ? "polygons" : "points"}`}
										size="small"
										variant="outlined"
									/>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								{isExpanded && groupMappings && (
									<PropertyMappingSelector
										geoJsons={[group.geoJSON]}
										propertyMappings={groupMappings}
										onMappingChange={groupCallbacks[group.id]}
										company={company}
										showMoreFields={vm.showMoreFields}
									/>
								)}
								{isExpanded && !groupMappings && (
									<Typography variant="body2" color="text.secondary">
										No property mappings configured for this group.
									</Typography>
								)}
							</AccordionDetails>
						</Accordion>
					);
				})}
			</Box>

			{geodataGroups.length === 0 && (
				<Box sx={{ textAlign: "center", py: 4 }}>
					<Typography variant="body1" color="text.secondary">
						No geodata groups available for property mapping.
					</Typography>
				</Box>
			)} */}
		</Box>
	);
}
